import { Header } from '@tanstack/react-table';
import { useSortable } from '@dnd-kit/sortable';
import { useMemo } from 'react';
import { CSS } from '@dnd-kit/utilities';
import './DraggableHeader.scss';
import { NSDropdown } from 'bricks';
import {
    UNIT_TYPE_ABBREVIATIONS, UnitType, ACTIONS_COLUMN_ID, SELECT_COLUMN_ID,
} from 'views/Deals/constants';
import SortingIcon from './SortingIcon/SortingIcon';
import { ExtendedColumnDef } from '../types';

type DraggableHeaderProps<TData> = {
    header: Header<TData, unknown>;
    sortedColumnId: string | null;
    sortedDirection: 'asc' | 'desc' | null;
    onAscendingSortPressed: (columnName: string) => void;
    onDescendingSortPressed: (columnName: string) => void;
    selectedUnitType: UnitType;
    handleSelectUnitType: (unitType: UnitType) => void;
};

export const DraggableHeader = <TData, >({
    header,
    sortedColumnId,
    sortedDirection,
    onAscendingSortPressed,
    onDescendingSortPressed,
    selectedUnitType,
    handleSelectUnitType,
}: DraggableHeaderProps<TData>) => {
    const isDraggable = header.column.id !== SELECT_COLUMN_ID && header.column.id !== ACTIONS_COLUMN_ID;
    const columnDef = header.column.columnDef as ExtendedColumnDef<TData>;
    const {
        includeDropdown,
        dropdownOptions,
        alignRight,
    } = columnDef;

    const {
        attributes, listeners, setNodeRef, transform, isDragging,
    } = useSortable({
        id: header.column.id,
        disabled: !isDraggable,
    });

    const columnHeader = useMemo(
        () => (typeof header.column.columnDef.header === 'function'
            ? header.column.columnDef.header(header.getContext())
            : String(header.column.columnDef.header)),
        [header],
    );

    return (
        <th
            ref={setNodeRef}
            {...(isDraggable ? attributes : {})}
            className={`DraggableHeader ${isDragging ? 'DraggableHeader--no-transition' : ''} ${!isDraggable ? 'DraggableHeader--fixed' : ''}`}
            style={{
                transform: CSS.Translate.toString(transform),
            }}
        >
            <div className="d-flex justify-content-between align-items-center w-100 DraggableHeader__content">
                <div className={`d-flex flex-column justify-content-center ${alignRight ? 'ml-auto text-right align-items-end' : 'align-items-start'}`}>
                    <div {...(isDraggable ? listeners : {})}>
                        {header.isPlaceholder ? null : columnHeader}
                    </div>
                    {includeDropdown && dropdownOptions && (
                        <NSDropdown
                            options={dropdownOptions}
                            handleSelectOption={option => handleSelectUnitType(option.value)}
                            selectedOption={{ label: UNIT_TYPE_ABBREVIATIONS[selectedUnitType], value: selectedUnitType }}
                            containerClassName="DraggableHeader__dropdown mt-1"
                            badgeColor="dark-lighten"
                            isBadgeToggle
                            useBodyContainer
                            menuRight
                        />
                    )}
                </div>

                {header.column.columnDef.enableSorting && (
                    <div className="ml-2 d-flex align-items-center">
                        <SortingIcon
                            isSorted={header.column.id === sortedColumnId}
                            sortedDirection={sortedDirection}
                            onAscendingSort={() => onAscendingSortPressed(header.column.id)}
                            onDescendingSort={() => onDescendingSortPressed(header.column.id)}
                            columnId={header.column.id}
                        />
                    </div>
                )}
            </div>
        </th>
    );
};
